<!--
  Promo banners for the agent portal, sitting under the supplier row of the search
  form. Because that form is rendered once and reused, this appears both on the
  entry page and above the search results without being placed twice.

  Deliberately NOT clickable: no <a>, no href, no pointer cursor. Promoting a deal
  here is a nudge, not a navigation.

  Desktop only, and every agent on the agent site sees it — internal and external.
  Managed from whap4u (אדמין אתר ← באנרים לסוכנים); the server drops inactive and
  expired banners, so anything that arrives here is live.
-->
<template>
  <div v-if="show" class="agent-promo-banners" :class="{ 'agent-promo-banners--results': isResultsPage }">
    <img
      v-for="banner in banners"
      :key="banner.id"
      :src="banner.imageUrl"
      :alt="banner.alt"
      class="agent-promo-banners__item"
      draggable="false"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

export default {
  name: 'AgentPromoBanners',
  computed: {
    ...mapGetters({
      banners: 'GET_AGENT_BANNERS',
      isOdysseySite: 'GET_ODYSSEY_AGENT_STATE',
      device: 'GET_DEVICE',
      currentPage: 'GET_CURRENT_PAGE',
    }),
    // The search form is rendered once and reused, so the banners have to work out
    // for themselves which page they are on: above the results they sit smaller, so
    // they take less of the room the results themselves need.
    isResultsPage() {
      return this.currentPage === 'search-result';
    },
    // Every agent on the agent site, internal and external alike. The gate is the
    // SITE, not the agent type — the white-label consumer sites built from this
    // same codebase must never show these.
    isAgentSite() {
      return this.isOdysseySite;
    },
    show() {
      return this.isAgentSite
        && this.device === 'desktop'
        && Array.isArray(this.banners)
        && this.banners.length > 0;
    },
  },
  mounted() {
    // Only fetch for the audience that can see them; the action is a no-op after
    // the first call.
    if (this.isAgentSite && this.device === 'desktop') {
      this.$store.dispatch('FETCH_AGENT_BANNERS');
    }
  },
};
</script>

<style scoped>
/*
  Starts from the right (RTL) and always lays out THREE per line, whatever the
  screen width — a fourth banner wraps.

  The size is set by capping the ROW rather than the banner: each banner takes a
  third of whatever the row is, so on a wide screen they land on exactly the
  intended width and on a narrow one they shrink together instead of re-flowing
  into an uneven two-and-one. Entry page 480px each, results page 320px — above
  the results the banners give way to the results themselves. Resizing a page is
  one number: 3 × the width you want, plus the two gaps.
*/
.agent-promo-banners {
  --gap: 16px;
  /* 3 × 480 + 2 gaps. The row never exceeds this, so on a wide screen the banners
     render at exactly the intended size instead of stretching. */
  --row-max: 1472px;
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-start;
  gap: var(--gap);
  max-width: var(--row-max);
  margin-top: 14px;
}

/* 3 × 320 + 2 gaps — half-size banners above the results. */
.agent-promo-banners--results {
  --row-max: 992px;
}

/*
  Fixed box with object-fit: cover, so a banner uploaded at the wrong ratio is
  cropped rather than allowed to distort or to push the search results down.
*/
.agent-promo-banners__item {
  /* A third of the row, gaps taken off first: ALWAYS three per line at any screen
     width, with the fourth wrapping. Narrower screens shrink the banners rather
     than re-flowing them, so the row never turns into two-and-one. */
  width: calc((100% - var(--gap) * 2) / 3);
  aspect-ratio: 16 / 9;
  flex: 0 0 auto;
  object-fit: cover;
  border-radius: 12px;
  display: block;
  user-select: none;
  pointer-events: none;
}
</style>
